import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';

import Column from 'components/common/Column';
import Row from 'components/common/Row';
import Text from 'components/common/Text';
import { ONLY_NUMBER_REGEX } from 'utils/regExp';
import { ICON_DROPDOWN } from 'assets/icons';
import TimeSelector from 'components/Input/TimeSelector';
import { TermDate } from '../hooks/useDateAdditionalHooks';

const TermContainer = styled.button<{ $isFocus: boolean; $disable: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  position: relative;

  padding: 12px;
  width: 100%;

  border-radius: 12px;
  border: ${props =>
    props.$isFocus ? '1.5px solid #1b1c1e' : '1px solid#eaebec'};

  background: ${props => (props.$disable ? '#f7f7f8' : '#fff')};
  cursor: ${props => (props.$disable ? 'auto' : 'pointer')};
`;

const CheckBox = styled.input.attrs({ type: 'checkbox' })`
  width: 24px;
  height: 24px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-color: #fff; /* 기본 배경색 */
  border: 1px solid #ddd; /* 테두리 */
  cursor: pointer;
  display: inline-block;
  position: relative;
  border-radius: 5px;

  &:checked {
    background-color: #000;
  }

  &:checked::before {
    content: '✓';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    color: white;
  }
`;

const OptionInput = styled.input`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 30px;

  text-align: right;
  border-radius: 8px;
  border: none;
  background: #fff;

  color: #414b56;

  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px; /* 137.5% */
  letter-spacing: -0.064px;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  ::placeholder {
    justify-content: flex-end;
  }
`;

const DateBox = styled(TermContainer)<{ $disable: boolean }>`
  &:focus-within {
    border: 1.5px solid #1b1c1e;
  }
`;

interface IsOpenTimeSelector {
  start: boolean;
  end: boolean;
}

export enum StartAmPm {
  AM = 'am',
  PM = 'pm',
}

interface StartEndAmPm {
  start: StartAmPm;
  end: StartAmPm;
}

interface Props {
  isMobile: boolean;
  isDisable: boolean;
  isOverDay: boolean;
  termDate: TermDate;
  setTermDate: Dispatch<SetStateAction<TermDate>>;
  handleSetOverDay: () => void;
}

const OptionTermDateV2: FC<Props> = props => {
  dayjs.locale('ko');
  const {
    isMobile,
    isOverDay,
    termDate,
    isDisable,
    setTermDate,
    handleSetOverDay,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpenTimeSelector, setIsOpenTimeSelector] =
    useState<IsOpenTimeSelector>({
      start: false,
      end: false,
    });
  const [startEndAmPm, setStartEndAmPm] = useState<StartEndAmPm>({
    start: StartAmPm.AM,
    end: StartAmPm.AM,
  });

  const formatTime = (value: string, amPm: StartAmPm) => {
    let [hour, minute] = value.split(':');

    hour = hour === '12' ? '12' : (Number(hour) - 12).toString();

    return `${hour}:${minute}`;
  };

  const handleOpenTimeSelector = (type: 'start' | 'end') => {
    if (isDisable) {
      return;
    }

    if (type === 'start') {
      setIsOpenTimeSelector(prev => ({
        ...prev,
        start: !prev.start,
        end: false,
      }));

      return;
    }

    setIsOpenTimeSelector(prev => ({
      ...prev,
      end: !prev.end,
      start: false,
    }));
  };

  const handleSetTermDate = (changes: Record<string, any>) => {
    setTermDate(prev => ({
      ...prev,
      ...changes,
    }));
  };

  // 시간 문자열을 분 단위 숫자로 변환하는 함수
  const convertTimeToMinutes = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const handleSetStartDate = (value: string, amPm: StartAmPm) => {
    if (isOverDay) {
      setTermDate(prev => ({
        ...prev,
        startDate: value,
      }));

      setStartEndAmPm(prev => ({
        ...prev,
        start: amPm,
      }));

      return;
    }

    if (
      termDate.endDate &&
      convertTimeToMinutes(termDate.endDate) < convertTimeToMinutes(value)
    ) {
      window.showToast(
        '종료시간은 시작 시간보다 빠를 수 없어요.',
        'failed',
        5000,
      );

      return;
    }

    setTermDate(prev => ({
      ...prev,
      startDate: value,
    }));

    setStartEndAmPm(prev => ({
      ...prev,
      start: amPm,
    }));
  };

  const handleSetEndDate = (value: string, amPm: StartAmPm) => {
    if (isOverDay) {
      setTermDate(prev => ({
        ...prev,
        endDate: value,
      }));
      setStartEndAmPm(prev => ({
        ...prev,
        end: amPm,
      }));

      return;
    }

    if (
      termDate.startDate &&
      convertTimeToMinutes(termDate.startDate) > convertTimeToMinutes(value)
    ) {
      window.showToast(
        '종료시간은 시작 시간보다 빠를 수 없어요.',
        'failed',
        5000,
      );

      return;
    }

    setTermDate(prev => ({
      ...prev,
      endDate: value,
    }));
    setStartEndAmPm(prev => ({
      ...prev,
      end: amPm,
    }));
  };

  useEffect(() => {
    setStartEndAmPm(prev => ({
      ...prev,
      start: termDate.startDate
        ? Number(termDate.startDate.split(':')[0]) >= 12
          ? StartAmPm.PM
          : StartAmPm.AM
        : StartAmPm.AM,
      end: termDate.endDate
        ? Number(termDate.endDate.split(':')[0]) >= 12
          ? StartAmPm.PM
          : StartAmPm.AM
        : StartAmPm.AM,
    }));
  }, [termDate]);

  const renderContents = (): JSX.Element => {
    return (
      <Column align="flex-start" gap="12px">
        <Column align="flex-start" gap="16px">
          <Text color="#414B56" size="18px" weight={600}>
            진행 시간
          </Text>
          <Row
            gap="12px"
            style={{
              flexDirection: isMobile ? 'column' : 'row',
              width: '100%',
            }}
          >
            <Row style={{ position: 'relative', width: '100%' }}>
              <TermContainer
                $disable={isDisable}
                $isFocus={isOpenTimeSelector.start}
                onClick={() => handleOpenTimeSelector('start')}
              >
                <Row gap="8px">
                  <Text
                    color={
                      isDisable
                        ? '#70737C'
                        : isOpenTimeSelector.start || termDate.startDate
                        ? '#414B56'
                        : '#C2C4C8'
                    }
                  >
                    {termDate.startDate
                      ? `${
                          startEndAmPm.start === StartAmPm.AM ? '오전' : '오후'
                        } ${
                          startEndAmPm.start === StartAmPm.AM
                            ? termDate.startDate
                            : formatTime(termDate.startDate, startEndAmPm.start)
                        }`
                      : '시작 시간'}
                  </Text>
                  <ICON_DROPDOWN
                    color={
                      isOpenTimeSelector.start || termDate.startDate
                        ? '#414B56'
                        : '#C2C4C8'
                    }
                    style={{
                      transform: isOpenTimeSelector.start
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                    }}
                  />
                </Row>
              </TermContainer>
              {isOpenTimeSelector.start && (
                <TimeSelector
                  isOverDay={isOverDay}
                  // hour={termDate.startDate.split(':')[0]}
                  // minute={termDate.startDate.split(':')[1]}
                  // amPm={startEndAmPm.start}
                  onClose={() => handleOpenTimeSelector('start')}
                  handleSetTermDate={handleSetStartDate}
                />
              )}
            </Row>
            {isOverDay && (
              <>
                <Text color="#989BA2" size="24px" weight={700}>
                  ~
                </Text>
                <DateBox ref={inputRef} $isFocus={false} $disable={isDisable}>
                  <OptionInput
                    value={termDate.durationDate}
                    // onFocus={() => inputRef.current?.focus()}
                    // onBlur={() => inputRef.current?.blur()}
                    onChange={e => {
                      handleSetTermDate({
                        durationDate: e.target.value.replaceAll(
                          ONLY_NUMBER_REGEX,
                          '',
                        ),
                      });
                    }}
                  />
                  <Text style={{ whiteSpace: 'nowrap' }}>일뒤</Text>
                </DateBox>
              </>
            )}
            <Text color="#989BA2" size="24px" weight={700}>
              ~
            </Text>
            <Row style={{ position: 'relative', width: '100%' }}>
              <TermContainer
                $isFocus={isOpenTimeSelector.end}
                onClick={() => handleOpenTimeSelector('end')}
                $disable={isDisable}
              >
                <Row gap="8px">
                  <Text
                    color={
                      isDisable
                        ? '#70737C'
                        : isOpenTimeSelector.end || termDate.endDate
                        ? '#414B56'
                        : '#C2C4C8'
                    }
                  >
                    {termDate.endDate
                      ? `${
                          startEndAmPm.end === StartAmPm.AM ? '오전' : '오후'
                        } ${
                          startEndAmPm.end === StartAmPm.AM
                            ? termDate.endDate
                            : formatTime(termDate.endDate, startEndAmPm.end)
                        }`
                      : '종료 시간'}
                  </Text>
                  <ICON_DROPDOWN
                    color={
                      isOpenTimeSelector.end || termDate.endDate
                        ? '#414B56'
                        : '#C2C4C8'
                    }
                    style={{
                      transform: isOpenTimeSelector.end
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                    }}
                  />
                </Row>
              </TermContainer>
              {isOpenTimeSelector.end && (
                <TimeSelector
                  isOverDay={isOverDay}
                  // hour={termDate.endDate.split(':')[0]}
                  // minute={termDate.endDate.split(':')[1]}
                  // amPm={startEndAmPm.end}
                  onClose={() => handleOpenTimeSelector('end')}
                  handleSetTermDate={handleSetEndDate}
                />
              )}
            </Row>
          </Row>
        </Column>
        <Row gap="6px">
          <CheckBox
            type="checkbox"
            checked={isOverDay}
            onChange={() => {
              if (isDisable) {
                return;
              }

              handleSetOverDay();
            }}
            style={{ width: '16px', height: '16px' }}
          />
          <Text
            color={isOverDay ? '#1B1C1E' : '#DBDCDF'}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              if (isDisable) {
                return;
              }

              handleSetOverDay();
            }}
          >
            24시간 이상 일정이에요
          </Text>
        </Row>
      </Column>
    );
  };

  return renderContents();
};

export default OptionTermDateV2;
